import { type ReactNode, useRef, useState, useCallback } from "react"
import { useDrag } from "@use-gesture/react"
import { cn } from "@/shared/utils/general/cn"
import { useBreakpoint } from "@/shared/hooks/useBreakpoint"
import { hapticLight, hapticMedium } from "@/shared/utils/platform/haptics"
import CheckIcon from "@/shared/icons/totodoicons/stroke/checkmark.svg?react"
import ThreeDotsIcon from "@/shared/icons/totodoicons/stroke/three-dots-vertical.svg?react"
import DeleteIcon from "@/shared/icons/totodoicons/stroke/delete.svg?react"
import ArrowRightIcon from "@/shared/icons/pikaicons/stroke/arrows-chevrons/arrow-right.svg?react"
import ArrowLeftIcon from "@/shared/icons/pikaicons/stroke/arrows-chevrons/arrow-left.svg?react"
import HeadingIcon from "@/shared/icons/pikaicons/stroke/editing/heading.svg?react"
import ChevronUpIcon from "@/shared/icons/pikaicons/stroke/arrows-chevrons/chevron-up.svg?react"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ChecklistSwipeAction =
  | "complete"   // L→R short: toggle done (items only)
  | "collapse"   // L→R short: toggle collapse (headlines only)
  | "indent"     // L→R long: increase depth
  | "headline"   // L→R long at max depth: convert to heading
  | "toItem"     // L→R long on headline: convert back to item
  | "menu"       // R→L short: status/progress menu
  | "outdent"    // R→L long: decrease depth
  | "delete"     // R→L long at root: remove item

export interface SwipeableChecklistRowProps {
  children: ReactNode
  onAction?: (action: ChecklistSwipeAction) => void
  disabled?: boolean
  /** Current indent depth (0-3). Determines edge-case actions. */
  depth?: number
  /** Whether this row is a section heading. Changes L→R behavior. */
  isHeadline?: boolean
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SHORT_THRESHOLD = 0.15
const LONG_THRESHOLD = 0.60
const ACTIVATION_PX = 8
const VELOCITY_MIN = 0.3
const MAX_DEPTH = 3

const COLOR_COMPLETE = "#22c55e"  // green-500
const COLOR_MENU = "#6b7280"      // gray-500
const COLOR_INDENT = "var(--primary)"
const COLOR_DELETE = "#ef4444"     // red-500
const COLOR_HEADLINE = "#f59e0b"  // amber-500
const COLOR_COLLAPSE = "#8b5cf6"  // violet-500

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SwipeableChecklistRow({
  children,
  onAction,
  disabled,
  depth = 0,
  isHeadline = false,
}: SwipeableChecklistRowProps) {
  const { isMobile } = useBreakpoint()
  const rowRef = useRef<HTMLDivElement>(null)
  const [offsetX, setOffsetX] = useState(0)
  const [bgColor, setBgColor] = useState("transparent")
  const [isTransitioning, setIsTransitioning] = useState(false)
  const hapticShortRef = useRef(false)
  const hapticLongRef = useRef(false)

  const getRowWidth = useCallback(
    () => rowRef.current?.offsetWidth ?? 300,
    [],
  )

  const computeAction = useCallback(
    (mx: number, rowWidth: number): ChecklistSwipeAction | null => {
      const pct = Math.abs(mx) / rowWidth
      if (pct < SHORT_THRESHOLD) return null
      const isRight = mx > 0
      if (isRight) {
        if (isHeadline) {
          // Headline: L→R short: collapse, long: convert back to item
          return pct >= LONG_THRESHOLD ? "toItem" : "collapse"
        }
        // Item: L→R short: complete, long: indent (headline at max depth)
        if (pct >= LONG_THRESHOLD) {
          return depth >= MAX_DEPTH ? "headline" : "indent"
        }
        return "complete"
      }
      // R→L short: menu, long: outdent (delete at root)
      if (pct >= LONG_THRESHOLD) {
        return depth <= 0 ? "delete" : "outdent"
      }
      return "menu"
    },
    [depth, isHeadline],
  )

  const computeBgColor = useCallback(
    (mx: number, rowWidth: number): string => {
      const pct = Math.abs(mx) / rowWidth
      if (pct < SHORT_THRESHOLD) return "transparent"
      const isRight = mx > 0
      if (isRight) {
        if (isHeadline) {
          return pct >= LONG_THRESHOLD ? COLOR_HEADLINE : COLOR_COLLAPSE
        }
        if (pct >= LONG_THRESHOLD) {
          return depth >= MAX_DEPTH ? COLOR_HEADLINE : COLOR_INDENT
        }
        return COLOR_COMPLETE
      }
      if (pct >= LONG_THRESHOLD) {
        return depth <= 0 ? COLOR_DELETE : COLOR_INDENT
      }
      return COLOR_MENU
    },
    [depth, isHeadline],
  )

  const bind = useDrag(
    ({ movement: [mx], velocity: [vx], last, cancel, memo }) => {
      if (disabled || !isMobile) {
        cancel?.()
        return memo
      }

      const rowWidth = getRowWidth()
      const pct = Math.abs(mx) / rowWidth

      if (!last) {
        if (pct >= SHORT_THRESHOLD && !hapticShortRef.current) {
          hapticShortRef.current = true
          hapticLight()
        }
        if (pct >= LONG_THRESHOLD && !hapticLongRef.current) {
          hapticLongRef.current = true
          hapticMedium()
        }

        let dampened = mx
        if (pct > LONG_THRESHOLD) {
          const excess = Math.abs(mx) - rowWidth * LONG_THRESHOLD
          dampened =
            Math.sign(mx) * (rowWidth * LONG_THRESHOLD + excess * 0.3)
        }

        setOffsetX(dampened)
        setBgColor(computeBgColor(mx, rowWidth))
        return memo
      }

      // Release
      hapticShortRef.current = false
      hapticLongRef.current = false

      let action = computeAction(mx, rowWidth)
      if (!action && vx > VELOCITY_MIN && pct > SHORT_THRESHOLD * 0.5) {
        action = mx > 0 ? (isHeadline ? "collapse" : "complete") : "menu"
      }

      if (action && onAction) {
        hapticMedium()
        onAction(action)
        const stop = (e: Event) => {
          e.stopPropagation()
          e.preventDefault()
        }
        document.addEventListener("click", stop, { capture: true, once: true })
        setTimeout(
          () =>
            document.removeEventListener("click", stop, { capture: true }),
          400,
        )
      }

      setIsTransitioning(true)
      setOffsetX(0)
      setBgColor("transparent")
      requestAnimationFrame(() => {
        setTimeout(() => setIsTransitioning(false), 300)
      })

      return memo
    },
    {
      axis: "x",
      filterTaps: true,
      pointer: { touch: true },
      threshold: ACTIVATION_PX,
    },
  )

  if (!isMobile) {
    return <>{children}</>
  }

  const isSwiping = offsetX !== 0
  const rowWidth = getRowWidth()
  const pct = Math.abs(offsetX) / rowWidth

  // Determine which icon to show based on direction + threshold
  const renderIcon = () => {
    if (!isSwiping) return null
    const isRight = offsetX > 0
    if (isRight) {
      if (isHeadline) {
        return pct >= LONG_THRESHOLD
          ? <HeadingIcon width={18} height={18} />
          : <ChevronUpIcon width={18} height={18} />
      }
      if (pct >= LONG_THRESHOLD) {
        return depth >= MAX_DEPTH
          ? <HeadingIcon width={18} height={18} />
          : <ArrowRightIcon width={18} height={18} />
      }
      return <CheckIcon width={18} height={18} />
    }
    if (pct >= LONG_THRESHOLD) {
      return depth <= 0
        ? <DeleteIcon width={18} height={18} />
        : <ArrowLeftIcon width={18} height={18} />
    }
    return <ThreeDotsIcon width={18} height={18} />
  }

  return (
    <div
      ref={rowRef}
      className={cn("relative overflow-hidden rounded-md")}
      style={{ touchAction: "pan-y" }}
    >
      {/* Reveal background */}
      <div
        className={cn(
          "absolute inset-0 flex items-center rounded-md",
          offsetX > 0 ? "justify-start pl-4" : "justify-end pr-4",
        )}
        style={{ backgroundColor: bgColor }}
        aria-hidden
      >
        {isSwiping && (
          <span className="flex items-center justify-center text-white">
            {renderIcon()}
          </span>
        )}
      </div>

      {/* Foreground content */}
      <div
        {...bind()}
        className="relative z-10 bg-card rounded-md"
        style={{
          transform: `translateX(${offsetX}px)`,
          transition: isTransitioning
            ? "transform 300ms cubic-bezier(0.25, 0.46, 0.45, 0.94)"
            : "none",
        }}
      >
        {children}
      </div>
    </div>
  )
}
